// Per-user usage counters (usage/{lowercased-email} — one small flat doc per tester).
// Client-trusted (acceptable behind the closed allowlist); counts are framed "approximate"
// in /admin. incrementGeneration is getDoc-first: the doc's ABSENCE means this is the first
// generation → stamp firstUsedAt; presence → OMIT it, so firstUsedAt never enters the rules'
// affectedKeys and the immutability guard passes. Both writers re-send byte-identical
// email+uid so value-diff keeps THEM out of affectedKeys too. Callers void these (see usage.ts).
import { collection, doc, getDoc, getDocs, setDoc, increment } from "firebase/firestore";
import { db } from "./firebase";
import type { Mode } from "@/types/roast";

export interface UsageEntry {
  email: string;
  uid: string | null;
  total: number;
  roast: number;
  toast: number;
  exports: number;
  firstUsedAt: string | null;
  lastUsedAt: string | null;
}

/** Bump total + the mode counter for a completed generation. */
export async function incrementGeneration(email: string, uid: string | null, mode: Mode): Promise<void> {
  const id = email.toLowerCase();
  const ref = doc(db, "usage", id);
  const now = new Date().toISOString();
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await setDoc(ref, { email: id, uid, total: increment(1), [mode]: increment(1), lastUsedAt: now }, { merge: true });
  } else {
    await setDoc(ref, { email: id, uid, total: increment(1), [mode]: increment(1), firstUsedAt: now, lastUsedAt: now }, { merge: true });
  }
}

/** Bump exports for a completed delivery. An export always follows a generation → the doc
 *  already exists → ALWAYS omit firstUsedAt. */
export async function incrementExport(email: string, uid: string | null): Promise<void> {
  const id = email.toLowerCase();
  await setDoc(doc(db, "usage", id), { email: id, uid, exports: increment(1), lastUsedAt: new Date().toISOString() }, { merge: true });
}

/** Admin-only read: every usage doc → UsageEntry, missing counters default to 0. */
export async function listUsage(): Promise<UsageEntry[]> {
  const snap = await getDocs(collection(db, "usage"));
  return snap.docs.map((d) => {
    const data = d.data() as Partial<UsageEntry>;
    return {
      email: d.id,
      uid: data.uid ?? null,
      total: data.total ?? 0,
      roast: data.roast ?? 0,
      toast: data.toast ?? 0,
      exports: data.exports ?? 0,
      firstUsedAt: data.firstUsedAt ?? null,
      lastUsedAt: data.lastUsedAt ?? null,
    };
  });
}
